import { useQuery } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';

interface AdminStats {
  totalUsers: number;
  totalArticles: number;
  clipsToday: number;
  activeUsersWeek: number;
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
  articleCount: number;
  joinedAt: string;
  lastActiveAt?: string | null;
}

interface TopUrl {
  url: string;
  title: string | null;
  saveCount: number;
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-xl border border-white/10 p-5 bg-surface-1">
      <p className="text-3xl font-bold" style={{ color }}>{value?.toLocaleString() ?? '—'}</p>
      <p className="text-sm mt-1 text-text-secondary">{label}</p>
    </div>
  );
}

export default function AdminPage() {
  const { user, isAdmin } = useAuth();

  const { data: stats } = useQuery<AdminStats>({
    queryKey: ['admin', 'stats'],
    queryFn: () => apiFetch('/admin/stats'),
    enabled: isAdmin
  });

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => apiFetch('/admin/users'),
    enabled: isAdmin
  });

  const { data: topUrls = [] } = useQuery<TopUrl[]>({
    queryKey: ['admin', 'top-urls'],
    queryFn: () => apiFetch('/admin/top-urls'),
    enabled: isAdmin
  });

  if (!isAdmin) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <ShieldOff size={56} className="mx-auto mb-4" style={{ color: '#F43F5E' }} />
            <h1 className="text-2xl font-bold text-white mb-2">403 Forbidden</h1>
            <p className="text-text-secondary">This area is restricted to admins.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-8">
          <h1 className="text-2xl font-bold text-white">Admin</h1>
          <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: '#8B5CF620', color: '#8B5CF6' }}>
            {user?.email}
          </span>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Users" value={stats.totalUsers} color="#8B5CF6" />
            <StatCard label="Total Articles" value={stats.totalArticles} color="#3B82F6" />
            <StatCard label="Clips Today" value={stats.clipsToday} color="#10B981" />
            <StatCard label="Active This Week" value={stats.activeUsersWeek} color="#F59E0B" />
          </div>
        )}

        {/* Top URLs */}
        {topUrls.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-semibold text-white mb-3">Top Saved URLs</h2>
            <div className="rounded-xl border border-white/5 overflow-hidden bg-surface-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-0">
                    <th className="text-left px-4 py-3 text-xs font-medium text-text-muted">#</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-text-muted">Title</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-text-muted">Saves</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {topUrls.map((u, i) => (
                    <tr key={u.url}>
                      <td className="px-4 py-3 text-xs font-mono text-text-muted">{i + 1}</td>
                      <td className="px-4 py-3">
                        <a href={u.url} target="_blank" rel="noopener noreferrer"
                          className="text-white hover:text-violet-300 transition-colors line-clamp-1">{u.title || u.url}</a>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold" style={{ color: '#8B5CF6' }}>{u.saveCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Recent users */}
        {users.length > 0 && (
          <section>
            <h2 className="text-base font-semibold text-white mb-3">Recent Users</h2>
            <div className="rounded-xl border border-white/5 overflow-hidden bg-surface-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-0">
                    <th className="text-left px-4 py-3 text-xs font-medium text-text-muted">Email</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-text-muted">Articles</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-text-muted">Joined</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-text-muted">Last active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="px-4 py-3 text-white">{u.email}</td>
                      <td className="px-4 py-3 text-right text-text-secondary">{u.articleCount}</td>
                      <td className="px-4 py-3 text-right text-xs text-text-muted">
                        {new Date(u.joinedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-text-muted">
                        {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
